import { computed, onScopeDispose, ref, watch } from 'vue'
import { defineStore } from 'pinia'
import type { Category, Comic } from '@tanko/shared'
import {
  AuthenticationRequiredError, CloudApiClient, CloudApiError,
  type CloudComicDetail, type CloudListedComic, type CloudProgressSummary, type CollectionSummary,
} from '../lib/api-client'
import { errorMessage } from '../lib/fs-access'
import { sortComics } from '../lib/comic-sort'
import { clearSelection, countWithin, pruneSelection, selectAll, selectRange, toggleSelection } from '../lib/comic-selection'
import { useLibraryDisplay } from '../lib/library-preferences'
import { clearUploadSession, mirrorCategories, pendingUploadsFor, readMirroredCollections, unbindCloudComic } from '../db/repo'
import { loadCollections } from '../lib/collection-service'
import { createCategory, deleteCategory, renameCategory } from '../lib/category-service'

type ComicListClient = Pick<CloudApiClient, 'comics'>

export async function loadCloudComics(api: ComicListClient, q: string, categoryIds: string[]): Promise<CloudListedComic[]> {
  const items = new Map<string, CloudListedComic>()
  const cursors = new Set<string>()
  let cursor: string | undefined
  do {
    const batch = await api.comics({ q: q.trim(), categoryIds: [...categoryIds], cursor })
    for (const comic of batch.items) if (!items.has(comic.id)) items.set(comic.id, comic)
    if (batch.nextCursor === null) cursor = undefined
    else {
      if (!batch.nextCursor || cursors.has(batch.nextCursor)) throw new Error('雲端書庫 cursor 不正確')
      cursors.add(batch.nextCursor)
      cursor = batch.nextCursor
    }
  } while (cursor !== undefined)
  return [...items.values()]
}

/** 雲端作品識別碼 → 目錄項上的進度摘要。沒有進度的作品不進 Map，卡片查不到就當作沒有。 */
export function indexCloudProgress(items: readonly CloudListedComic[]): Map<string, CloudProgressSummary> {
  const index = new Map<string, CloudProgressSummary>()
  for (const comic of items) if (comic.progress) index.set(comic.id, comic.progress)
  return index
}

const api = new CloudApiClient()

export const useCloudLibraryStore = defineStore('cloud-library', () => {
  const cloudComics = ref<CloudListedComic[]>([])
  /** 雲端那一份的進度，鍵是雲端識別碼；已註冊的作品在任何裝置上都用同一個值查。 */
  const progress = computed(() => indexCloudProgress(cloudComics.value))
  const categories = ref<Category[]>([])
  const categoryCounts = ref(new Map<string, number>())
  const query = ref('')
  const selectedCategories = ref<string[]>([])
  const selectionMode = ref(false)
  const selection = ref<ReadonlySet<string>>(new Set())
  /** shift 範圍選取的錨點：上一次單獨點選的那一本。 */
  const selectionAnchor = ref<string>()
  /**
   * 每個隱私分類底下有哪些雲端作品。
   *
   * 取不到時維持上一份而不是清空——寧可多藏一本，不可以在取不到資料時把該藏的攤出來。
   */
  const privateMembers = ref<readonly { categoryId: string; comicIds: string[] }[]>([])
  /**
   * 集合。與清單同一輪取得，不會有「清單更新了、集合還是舊的」那一瞬間；取不到時退回
   * 鏡像——書架的折疊結果不該被網路擋住。
   */
  const collections = ref<readonly CollectionSummary[]>([])
  const ready = ref(false)
  const refreshing = ref(false)
  const authenticationRequired = ref(false)
  const error = ref('')
  const stale = ref(false)
  let initialized = false
  let timer: ReturnType<typeof setTimeout> | undefined
  let generation = 0

  // 雲端清單一次把所有分頁抓完後放在前端，因此排序在前端做，不動 API。
  const display = useLibraryDisplay()
  const comics = computed<Comic[]>(() => sortComics(cloudComics.value.map(comic => ({
    id: comic.id, libraryId: 'cloud', relPath: '', kind: 'folder', title: comic.title, author: comic.author,
    pageCount: comic.pageCount, indexed: true, coverPage: comic.coverPage,
    cloudComicId: comic.id, syncState: comic.syncState, addedAt: comic.createdAt,
  })), display.sortOrder.value))

  function failed(cause: unknown) {
    authenticationRequired.value = cause instanceof AuthenticationRequiredError
    error.value = errorMessage(cause)
  }

  async function refresh(includeCategories = false) {
    const token = ++generation
    refreshing.value = true
    error.value = ''
    authenticationRequired.value = false
    try {
      const [items, cloudCategories, hidden, cloudCollections] = await Promise.all([
        loadCloudComics(api, query.value, selectedCategories.value),
        includeCategories ? api.categories().then(result => result.items) : Promise.resolve(undefined),
        // 與清單同一輪取得，才不會有「清單已更新、隱藏名單還是舊的」那一瞬間。
        api.privateCategoryMembers().then(result => result.items).catch(() => undefined),
        loadCollections(api).catch(() => undefined),
      ])
      if (token !== generation) return
      cloudComics.value = items
      if (hidden) privateMembers.value = hidden
      if (cloudCollections) collections.value = cloudCollections
      if (cloudCategories) {
        categories.value = cloudCategories.map((category, index) => ({ id: category.id, name: category.name, sortOrder: index, private: category.private }))
        categoryCounts.value = new Map(cloudCategories.map(category => [category.id, category.count]))
        const ids = new Set(cloudCategories.map(category => category.id))
        // 只在真的有分類被移除時才寫回去：把相等的新陣列賦回去足以喚醒自己的 deep
        // watcher，於是排了第二次一模一樣的請求——每一次重新整理都會變成兩次。
        const kept = selectedCategories.value.filter(id => ids.has(id))
        if (kept.length !== selectedCategories.value.length) selectedCategories.value = kept
        // 同步本地鏡像：認證失效或離線時，分類名稱與本地作品的篩選仍要能運作。
        await mirrorCategories(cloudCategories)
      }
    } catch (cause) { if (token === generation) failed(cause) }
    finally {
      if (token === generation) { refreshing.value = false; ready.value = true }
    }
  }

  async function initialize() {
    if (initialized && !stale.value) return
    initialized = true
    stale.value = false
    // 鏡像先到：離線或登入過期時書架仍能折出集合格；雲端回來後整份取代。
    if (!collections.value.length) collections.value = await readMirroredCollections().catch(() => [])
    await refresh(true)
  }

  /** 閱讀器改了封面等雲端權威資料後標記過期，讓下一次進入書庫重新取得而非沿用初始化快取。 */
  function invalidate() { stale.value = true }

  function scheduleRefresh() {
    if (!initialized) return
    if (timer) clearTimeout(timer)
    timer = setTimeout(() => { void refresh() }, 250)
  }
  watch(query, scheduleRefresh)
  watch(selectedCategories, scheduleRefresh, { deep: true })

  async function previewCount(categoryIds: string[]): Promise<number> {
    return (await loadCloudComics(api, query.value, categoryIds)).length
  }

  /** 分類管理頁展開某個分類時取該分類的作品；不受書庫頁目前的搜尋字串影響。 */
  async function comicsInCategory(categoryId: string): Promise<Comic[]> {
    const items = await loadCloudComics(api, '', [categoryId])
    return items.map(comic => ({
      id: comic.id, libraryId: 'cloud', relPath: '', kind: 'folder' as const, title: comic.title, author: comic.author,
      pageCount: comic.pageCount, indexed: true, coverPage: comic.coverPage,
      cloudComicId: comic.id, syncState: comic.syncState, addedAt: comic.createdAt,
    }))
  }

  async function saveCategory(name: string, isPrivate: boolean, id?: string) {
    // 走同一個寫入入口，鏡像因此在每個路徑上都跟著更新。
    if (id) await renameCategory(id, name, isPrivate)
    else await createCategory(name, isPrivate)
    await refresh(true)
  }

  async function removeCategory(id: string) {
    await deleteCategory(id)
    selectedCategories.value = selectedCategories.value.filter(value => value !== id)
    await refresh(true)
  }

  function detail(id: string): Promise<CloudComicDetail> { return api.comic(id) }
  async function saveComic(id: string, patch: { title: string; author: string; coverPage: number; categoryIds: string[] }) {
    await api.updateComic(id, patch)
    await refresh(true)
  }
  /** 批次刪除：一次請求、原子套用。 */
  async function deleteComics(comicIds: readonly string[]) {
    await api.bulkDeleteComics(comicIds)
    dropFromSelection(comicIds)
    await refresh(true)
  }

  async function deleteComic(id: string) {
    // 尚未發布的 session 不被目錄項參照，伺服器端因此推不出要清哪個 R2 prefix——只刪目錄項
    // 的話，已上傳的分片與物件會留到七天 TTL 才被清掉。對應關係只有本機這張表知道。
    // 先取消再刪作品：取消失敗時兩邊都還在，看得到原因也還能重試。
    for (const session of await pendingUploadsFor(id)) {
      try { await api.cancelUpload(session.uploadId) }
      catch (cause) { if (!(cause instanceof CloudApiError && cause.status === 404)) throw cause }
      await clearUploadSession(session.comicId, session.uploadId)
    }
    await api.deleteComic(id)
    // 只刪雲端副本：解除本地綁定後，本地檔案與閱讀狀態仍可使用。
    await unbindCloudComic(id)
    await refresh(true)
  }

  /**
   * 封面的快取版本。換封面會同時更新 comics.updated_at，因此它一變就代表封面可能變了。
   * 封面本身由 useComicCover 取得，那裡只拿得到對映後的 Comic，所以版本由這裡查。
   */
  const coverVersions = computed(() => new Map(cloudComics.value.map(comic => [comic.id, comic.updatedAt])))
  function coverVersion(id: string): number { return coverVersions.value.get(id) ?? 0 }

  const selectedCount = computed(() => selection.value.size)
  /** 選取中有多少本落在目前的篩選結果內。雲端的篩選在伺服器端做，comics 就是結果。 */
  const selectedInFilter = computed(() => countWithin(selection.value, comics.value.map(comic => comic.id)))
  const selectedComics = computed(() => comics.value.filter(comic => selection.value.has(comic.id)))
  function enterSelectionMode() { selectionMode.value = true }
  function exitSelectionMode() {
    selectionMode.value = false
    selection.value = clearSelection()
    selectionAnchor.value = undefined
  }
  /** `extend` 為 shift 點擊：從上一次單獨點選的那一本連選到這一本。 */
  function toggleSelected(id: string, extend = false) {
    const anchor = selectionAnchor.value
    if (extend && anchor && anchor !== id) {
      selection.value = selectRange(selection.value, comics.value.map(comic => comic.id), anchor, id)
      return
    }
    selection.value = toggleSelection(selection.value, id)
    selectionAnchor.value = id
  }
  function selectAllFiltered() { selection.value = selectAll(selection.value, comics.value.map(comic => comic.id)) }
  function clearSelected() { selection.value = clearSelection(); selectionAnchor.value = undefined }
  function dropFromSelection(ids: readonly string[]) {
    const removed = new Set(ids)
    selection.value = new Set([...selection.value].filter(id => !removed.has(id)))
  }
  // 重新取得清單後修剪選取，讓本數不可能指到已不存在的雲端作品。
  watch(cloudComics, items => {
    selection.value = pruneSelection(selection.value, new Set(items.map(comic => comic.id)))
  })

  onScopeDispose(() => { if (timer) clearTimeout(timer) })
  return {
    comics, cloudComics, progress, collections, categories, categoryCounts, privateMembers, query, selectedCategories,
    ready, refreshing, authenticationRequired, error,
    initialize, invalidate, refresh, previewCount, comicsInCategory, saveCategory, removeCategory, detail, saveComic, deleteComic, deleteComics, coverVersion,
    selectionMode, selection, selectedCount, selectedInFilter, selectedComics,
    enterSelectionMode, exitSelectionMode, toggleSelected, selectAllFiltered, clearSelected, dropFromSelection,
  }
})
